<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { FilterMatchMode } from '@primevue/core/api';
import type { ConfigModule } from '~/types/config';
import { removeColon } from '~/Common/StringOperations';
import { flattenConfigModules } from '~/Common/ConfigHelpers';
import { useConfigStore } from '~/stores/config';
import { useConfigParserStore } from '~/stores/configParser';
import { ConfigChangesContextMenu } from '~/Common/ContextMenu';


const props = defineProps<{
  visible: boolean;
  changes: ConfigModule[];
}>();

const emit = defineEmits<{
  'update:visible': [visible: boolean];
  'navigate-to-field': [{ moduleId: number; xPath: string; elementId: number; }];
}>();

const { t } = useI18n();
const configStore = useConfigStore();
const configParserStore = useConfigParserStore();

// Local state for the dialog visibility
const dialogVisible = ref(props.visible);


// State for expanded rows - use the flattened type
const expandedRows = ref<typeof flattenedChanges.value>([]);

// Add selection state for multiple row selection
const selectedChanges = ref<typeof flattenedChanges.value>([]);

// Add a computed property for better reactivity
const selectedChangesCount = computed(() => selectedChanges.value.length);
const hasSelectedChanges = computed(() => selectedChangesCount.value > 0);

// Add initial multi-sort configuration
const multiSortMeta = ref([
  { field: 'moduleName', order: 1 },
  { field: 'categoryName', order: 2 },
  { field: 'headline', order: 3 },
  { field: 'translation', order: 4 }
]);

// Replace the manual filter state with DataTable filters
const filters = ref({
  moduleName: { value: null, matchMode: FilterMatchMode.CONTAINS },
  categoryName: { value: null, matchMode: FilterMatchMode.CONTAINS },
  headline: { value: null, matchMode: FilterMatchMode.CONTAINS },
  translation: { value: null, matchMode: FilterMatchMode.CONTAINS }
});

// Add filter visibility state
const filterVisible = ref({
  moduleName: false,
  categoryName: false,
  headline: false,
  translation: false
});

// Synchronize local state with the prop
watch(() => props.visible, (newValue) => {
  dialogVisible.value = newValue;
});

// Synchronize changes back to the parent
watch(dialogVisible, (newValue) => {
  if (newValue !== props.visible) {
    emit('update:visible', newValue);
  }
  // Clear selection when dialog closes
  if (!newValue) {
    selectedChanges.value = [];
  }
});


// Function to get original element value from config store
const getOriginalElementValue = (elementId: number): string => {
  const lookupResult = configStore.getElementLookupMap.get(elementId);
  return lookupResult?.element.elementValue || '';
};

// Flatten the module structure for DataTable display using the helper function
const flattenedChanges = computed(() => {
  return flattenConfigModules(props.changes, getOriginalElementValue);
});

/**
 * Toggle the visibility of a specific filter field
 * @param field - The field to toggle visibility for
 */
const toggleFilter = (field: keyof typeof filterVisible.value) => {
  filterVisible.value[field] = !filterVisible.value[field];
};

/**
 * Navigate to the specific field in the configuration
 * @param change - The change object containing moduleId, elementPath, and id
 */
const navigateToField = (change: typeof flattenedChanges.value[0]) => {
  // Close dialog and emit navigation event
  dialogVisible.value = false;
  emit('navigate-to-field', {
    moduleId: change.moduleId,
    xPath: change.elementPath,
    elementId: change.id
  });
};

// Function to undo a single change
const undoChange = (change: typeof flattenedChanges.value[0]) => {
  const values: Record<string, string> = {
    [change.elementPath]: change.originalValue || ''
  };
  configParserStore.undoChanges(values);
}

// Function to undo all selected changes
const undoSelectedChanges = () => {
  if (selectedChanges.value.length === 0) return;

  // Convert selected changes to Record<string, string> format
  const values: Record<string, string> = {};
  selectedChanges.value.forEach(change => {
    values[change.elementPath] = change.originalValue || '';
  });

  configParserStore.undoChanges(values)
  // Clear the selection after emitting
  selectedChanges.value = [];
};


// Function to expand all rows
const expandAll = () => {
  expandedRows.value = [...flattenedChanges.value];
};

// Function to collapse all rows
const collapseAll = () => {
  expandedRows.value = [];
};


// Function to toggle selection of a single change
const toggleSelection = (change: typeof flattenedChanges.value[0]) => {
  const index = selectedChanges.value.findIndex(item => item.id === change.id);
  if (index > -1) {
    selectedChanges.value.splice(index, 1);
  } else {
    selectedChanges.value.push(change);
  }
};

// Function to check if a change is selected
const isSelected = (change: typeof flattenedChanges.value[0]) => {
  return selectedChanges.value.some(item => item.id === change.id);
};

// Function to select all changes
const selectAll = () => {
  selectedChanges.value = [...flattenedChanges.value];
};

const deselectAll = () => {
  selectedChanges.value = [];
};

// Function to handle actions from the context menu
function handleSendAction(action: string) {
  if (action === 'expandAll') {
    expandAll();
  } else if (action === 'collapseAll') {
    collapseAll();
  } else if (action === 'selectAll') {
    selectAll();
  } else if (action === 'deselectAll') {
    deselectAll();
  }
}

// Replace the local menuItems with the imported function
const menuItems = computed(() => {
  const contextMenuFactory = ConfigChangesContextMenu(
    selectedChangesCount.value,
    flattenedChanges.value.length,
    handleSendAction
  );
  return contextMenuFactory();
});
</script>

<template>
  <Dialog
    :visible="dialogVisible"
    @update:visible="dialogVisible = $event"
    :header="$t('common.dialogs.changes.title')"
    modal
    dismissable-mask
    class="w-full"
    :style="{ maxWidth: '1400px', width: '98vw' }"
  >
    <div class="p-3">
      <p class="mb-3 text-sm">
        {{ flattenedChanges.length === 1 ?
          $t('common.dialogs.changes.description', { count: flattenedChanges.length }) :
          $t('common.dialogs.changes.descriptionPlural', { count: flattenedChanges.length })
        }}
      </p>

      <DataTable
        v-model:filters="filters"
        :value="flattenedChanges"
        scrollable
        scroll-height="600px"
        class="p-datatable-sm"
        size="small"
        v-model:expandedRows="expandedRows"
        sortMode="multiple"
        removableSort
        :multiSortMeta="multiSortMeta"
        filterDisplay="row"
        dataKey="id"
      >
        <template #empty>
          <Message
            severity="info"
            :closable="false"
            class="mb-3"
          >
            {{ $t('common.dialogs.changes.noChanges') }}
          </Message>
        </template>

        <template #header>
          <div class="flex gap-2 justify-content-between">
            <Button
              :label="selectedChangesCount === 1 ?
                  $t('common.dialogs.changes.undoSelectedChange', { count: selectedChangesCount }) :
                  $t('common.dialogs.changes.undoSelectedChanges', { count: selectedChangesCount })
                "
              type="button"
              icon="pi pi-undo"
              size="small"
              severity="secondary"
              @click="undoSelectedChanges"
              :disabled="!hasSelectedChanges"
            />

            <BaseMenuButton
              class="ml-auto"
              :items="menuItems"
            />
          </div>
        </template>

        <Column expander class="w-3rem" />

        <template #expansion="slotProps">
          <div class="grid col-12 p-3 mb-3">
            <code-diff
              :old-string="slotProps.data.originalValue || ''"
              :new-string="slotProps.data.elementValue || ''"
              language="text"
              output-format="side-by-side"
              :hide-line-numbers="true"
              class="text-xs"
            />
          </div>
        </template>

        <Column
          field="moduleName"
          class="w-auto"
          sortable="true"
        >
          <template #header>
            <div class="flex align-items-center gap-2">
                <span>
                  {{ $t('config.form.module') }}
                </span>
              <Button
                icon="pi pi-filter"
                text
                size="small"
                @click="toggleFilter('moduleName')"
                :severity="filters.moduleName.value ? 'info' : 'secondary'"
              />
            </div>
          </template>
          <template #filter="{ filterModel, filterCallback }" v-if="filterVisible.moduleName">
            <InputText
              v-model="filterModel.value"
              type="text"
              @input="filterCallback()"
              class="p-column-filter"
              :placeholder="$t('common.actions.filter')"
              size="small"
            />
          </template>
        </Column>
        <Column
          field="categoryName"
          class="w-auto"
          sortable="true"
        >
          <template #header>
            <div class="flex align-items-center gap-2">
                <span>
                  {{ $t('config.form.category') }}
                </span>
              <Button
                icon="pi pi-filter"
                  text
                size="small"
                @click="toggleFilter('categoryName')"
                :severity="filters.categoryName.value ? 'info' : 'secondary'"
              />
            </div>
          </template>
          <template #filter="{ filterModel, filterCallback }" v-if="filterVisible.categoryName">
            <InputText
              v-model="filterModel.value"
              type="text"
              @input="filterCallback()"
              class="p-column-filter"
              :placeholder="$t('common.actions.filter')"
              size="small"
            />
          </template>
        </Column>
        <Column
          field="headline"
          class="w-auto"
          sortable="true"
        >
          <template #header>
            <div class="flex align-items-center gap-2">
                <span>
                  {{ $t('config.form.headline') }}
                </span>
              <Button
                icon="pi pi-filter"
                text
                size="small"
                @click="toggleFilter('headline')"
                :severity="filters.headline.value ? 'info' : 'secondary'"
              />
            </div>
          </template>
          <template #filter="{ filterModel, filterCallback }" v-if="filterVisible.headline">
            <InputText
              v-model="filterModel.value"
              type="text"
              @input="filterCallback()"
              class="p-column-filter"
              :placeholder="$t('common.actions.filter')"
              size="small"
            />
          </template>
        </Column>
        <Column
          field="translation"
          class="w-auto"
          sortable="true"
        >
          <template #header>
            <div class="flex align-items-center gap-2">
                <span>
                  {{ $t('config.form.field') }}
                </span>
              <Button
                icon="pi pi-filter"
                text
                size="small"
                @click="toggleFilter('translation')"
                :severity="filters.translation.value ? 'info' : 'secondary'"
              />
            </div>
          </template>
          <template #filter="{ filterModel, filterCallback }" v-if="filterVisible.translation">
            <InputText
              v-model="filterModel.value"
              type="text"
              @input="filterCallback()"
              class="p-column-filter"
              :placeholder="$t('common.actions.filter')"
              size="small"
            />
          </template>
          <template #body="slotProps">
            {{ removeColon(slotProps.data.translation) }}
          </template>
        </Column>
        <Column
          field="actions"
          :header="$t('common.actions.actions')"
          class="text-center w-auto"
        >
          <template #body="slotProps">
            <div class="flex justify-content-start align-items-center gap-2 h-full">
              <Checkbox
                :modelValue="isSelected(slotProps.data)"
                @change="toggleSelection(slotProps.data)"
                :binary="true"
                v-tooltip.top="$t('common.actions.select')"
                class=""
              />
              <Button
                severity="secondary"
                type="button"
                icon="pi pi-undo"
                size="small"
                @click.stop="undoChange(slotProps.data)"
                v-tooltip.top="$t('common.actions.undo')"
              />
              <Button
                severity="info"
                type="button"
                icon="pi pi-external-link"
                size="small"
                @click="navigateToField(slotProps.data)"
                v-tooltip.top="$t('common.dialogs.changes.goToField')"
              />
            </div>
          </template>
        </Column>
      </DataTable>
    </div>
  </Dialog>
</template>